import { HttpError, useLogin, useNotification, useCustomMutation } from "@refinedev/core";
import { Box, Button, Typography } from "@mui/material";
import { TeziLogoWithTextTP } from "../../assests/icons/tezi";
import { Link } from "react-router";
import { ArrowBack } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import OtpInput from "../../components/OtpInput";
import { useEffect } from "react";

// Add this prop type at the top
type VerifyOtpPageProps = {
  onBack: () => void;
  sessionId: string;
};

interface IVerifyOtpForm {
  otp: string;
  canResend: boolean;
  timer: number;
}

export const VerifyOtpPage: React.FC<VerifyOtpPageProps> = ({ onBack, sessionId }) => {
  const { mutate: login } = useLogin();
  const { open } = useNotification();
  const { mutate: sendOtp } = useCustomMutation();
  const phoneNumber = localStorage.getItem("phoneNumber");

  const {
    control,
    setValue,
    watch,
    handleSubmit,
  } = useForm<IVerifyOtpForm>({
    defaultValues: {
      otp: import.meta.env.DEV ? "000000" : "",
      canResend: false,
      timer: 30,
    },
  });

  const timer = watch("timer");
  const canResend = watch("canResend");

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timer > 0) {
      interval = setInterval(() => {
        setValue("timer", timer - 1);
        if (timer === 1) {
          setValue("canResend", true);
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer, setValue]);

  const handleResendOtp = () => {
    if (!canResend) return;

    sendOtp({
      url: `/auth/resend-otp`,
      method: "post",
      values: {
        session_id: sessionId
      },
      successNotification: (data: any) => {
        if (data?.data?.session_id) {
          setValue("timer", 30);
          setValue("canResend", false);
          return {
            type: "success",
            message: "OTP resent successfully",
          };
        }
        return false;
      },
    });
  };

  const onSubmit = handleSubmit((data: IVerifyOtpForm) => {
    if (data.otp.length !== 6) {
      open?.({
        type: "error",
        message: "Please enter a valid 6-digit OTP",
      });
      return;
    }

    login(
      {
        otp: data.otp,
        session_id: sessionId,
      },
      {
        onSuccess: () => {
          open?.({
            type: "success",
            message: "OTP verified successfully",
            description: "You can now login to your account",
          });
        },
        onError: (error: Partial<HttpError>) => {
          open?.({
            type: "error",
            message: error.response?.data?.error || "Invalid OTP",
          });
        },
      }
    );
  });

  return (
    <Box sx={{ maxWidth: 400, margin: "auto" }}>
      <Box textAlign="center" mb={4}>
        <Link to="/">
          <TeziLogoWithTextTP width={200} height="auto"/>
        </Link>
      </Box>
      
      <Box component="form" onSubmit={onSubmit}>
        
        {onBack && (
          <Button 
            onClick={onBack} 
            sx={{ mb: 1 }}
            startIcon={<ArrowBack />}
          >
            Back to Phone Number
          </Button>
        )}

        <Typography variant="body1" sx={{ mb: 2, textAlign: "center" }}>
          Verification code sent to <Typography  component="span" fontWeight={600} variant="body1" sx={{ mb: 3, textAlign: "center" }}>
            +91 {phoneNumber}
          </Typography>
        </Typography>

        <Box sx={{ mb: 3 }}>
          <OtpInput
            name="otp"
            control={control}
            length={6}
          />
        </Box>

        <Button fullWidth variant="contained" type="submit" sx={{ mb: 2 }}>
          Verify OTP
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            onClick={handleResendOtp}
            disabled={!canResend}
            sx={{ textTransform: 'none' }}
          >
            {canResend ? (
              "Resend OTP"
            ) : (
              `Resend OTP in ${timer}s`
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}; 